import React, { useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectAllProductsByLocale, fetchProductsNextPage, fetchProducts, fetchProductsByCategory,
} from 'models/products';
import { LocalizedProduct } from 'models/types/products';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import styled from 'styled-components';
import FooterV2 from 'components/footer_v2';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';
import ProductFilter from 'components/filter/ProductFilter';
import ProductItem from 'components/product/ProductItem';
import { useTranslation } from 'react-i18next';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import {
  selectCheckedProductIds,
} from 'models/productCategories/selected';
import { useMediaQuery } from '@material-ui/core';
import ProductMobileFilter from 'components/filter/ProductMobileFilter';
import { FETCH_ASSETS_URL } from 'appenv';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import Routes from 'Routes';

const EmptyStateImg = `${FETCH_ASSETS_URL}/assets/search-empty-state.svg`;

const RootContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 100%;
  overflow: auto;
  flex-direction: column;

  @media (min-width: 960px) {
    flex-direction: row;
  }
`;

const ProductListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 8px 16px;
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow: auto;
`;

const FilterContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 959px) {
    flex: 0;
  }
`;

const EmptyStateContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const EmptyStateText = styled.div`
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 16px;
  color: #484848;
  padding: 24px;
`;

const ProductListWrapper = ({ productList }: {
  productList: LocalizedProduct[];
}) => {
  const { chatroomConfig = {} } = useSelector((s: any) => s.eventConfigs);
  const hideChatLink = useMemo(() => isChatroomDisabled(chatroomConfig), [chatroomConfig]);

  return (
    <>
      {productList.map((product) => (
        <ProductItem product={product} key={product.id} hideChatLink={hideChatLink} />
      ))}
    </>
  );
};

const ProductPageLoadingContainer = styled.div`
  flex-basis: 100%;
`;

const FooterContainer = styled.div`
  flex-basis: 100%;
  margin-bottom: 16px;
`;

const ProductPageLoadingIndicator = () => (
  <ProductPageLoadingContainer>
    <CircularLoadingIndicator />
  </ProductPageLoadingContainer>
);

const ProductFilterSwitch = () => {
  const isDesktop = useMediaQuery('(min-width: 960px)');
  return isDesktop ? <ProductFilter /> : <ProductMobileFilter />;
};

const ProductList = () => {
  const dispatch = useDispatch();
  const productList: LocalizedProduct[] = useSelector(selectAllProductsByLocale);
  const { products: { hideProductFilter } = { hideProductFilter: false } } = useSelector((state) => state.eventConfigs);
  const fetching = useSelector((state: any) => state.products.fetching);
  const hasNextPage = useSelector((state: any) => state.products.pageInfo.hasNextPage);
  const { t } = useTranslation();
  const containerRef: React.MutableRefObject<HTMLDivElement> = React.useRef();

  const fetchNextPage = useCallback(() => {
    const { scrollHeight, clientHeight, scrollTop } = containerRef.current;
    if (scrollHeight - clientHeight - scrollTop < 500 && !fetching && hasNextPage) {
      dispatch(fetchProductsNextPage());
    }
  }, [fetching, dispatch, hasNextPage]);

  const selectedProductCategories = useSelector(selectCheckedProductIds);
  const selectedProductCategoriesMap = useMemo(() => selectedProductCategories.reduce(
    (acc, curr) => {
      acc[curr] = true;
      return acc;
    }, {},
  ), [selectedProductCategories]);

  useEffect(() => {
    if (selectedProductCategories.length) {
      dispatch(fetchProductsByCategory({
        categories: selectedProductCategoriesMap,
      }));
    } else {
      dispatch(fetchProducts());
    }
  }, [dispatch, selectedProductCategories.length, selectedProductCategoriesMap]);

  return (
    <RootContainer>
      {!hideProductFilter && (
        <FilterContainer>
          <ProductFilterSwitch />
        </FilterContainer>
      )}
      <ScrollableContainerWrapper
        WrapperContainer={ProductListContainer}
        refOverride={containerRef}
        onScroll={fetchNextPage}
      >
        {!!productList.length && <ProductListWrapper productList={productList} />}
        {(!productList.length && !fetching) && (
          <EmptyStateContainer>
            <img src={EmptyStateImg} alt="Coming Soon" />
            <EmptyStateText>
              {t('product.empty', 'No Products')}
            </EmptyStateText>
          </EmptyStateContainer>
        )}
        {fetching && (
          <ProductPageLoadingIndicator />
        )}
        <FooterContainer>
          <FloatingFooter hashState={Routes.products} />
        </FooterContainer>
      </ScrollableContainerWrapper>
    </RootContainer>
  );
};

export default ProductList;
